import { Card, HTMLSelect, NumericInput, Tag } from "@blueprintjs/core";
import {
    segmentTrackBusPageFuelTypeChanged,
    segmentTrackBusPageProjectSizeChanged,
    segmentTrackBusPageScopeChanged
} from "analytics/analyticTrackEvents";
import {
    getScopesForFuelType,
    useBusPageConfiguration
} from "contexts/BusPageContext/BusPageConfigurationState";
import React, { useEffect, useRef } from "react";
import {
    GeneratorType,
    GeneratorTypeIcon,
    getLabelForRegionAndGeneratorType,
    STORAGE_TYPE
} from "types/generatorType";
import {
    CAPACITY_RESOURCE_TYPE,
    CHARGING_RESOURCE_TYPE,
    ENERGY_RESOURCE_TYPE,
    ResourceType
} from "types/resourceType";
import { isLocalStudy, Scope } from "types/scope";
import { getGenerationFuelTypesForRegion } from "types/scopeViewType";
import "./BusConfigurationCard.scss";

const renderScopeOptions = (scopes: ReadonlyArray<Scope>) =>
    scopes.map((scope) => (
        <option key={scope.scope} value={scope.scope}>
            {scope.display_name}
        </option>
    ));

const MAX_MW = 1000;
const MIN_MW = 0;
const STEP_SIZE = 10;
const PROJECT_SIZE_TRACK_DEBOUNCE_MS = 1500;

const BusConfigurationCard: React.FC = () => {
    const {
        bus,
        niraBusId,
        pricedConstraintConfig,
        selectedGeneratorType,
        setSelectedGeneratorType,
        allScopesForBus,
        scopeToFuelTypesForBus,
        selectedScope,
        setSelectedScope,
        powerAmounts,
        setPowerAmounts
    } = useBusPageConfiguration();

    const getAvailableScopesForType = (type: GeneratorType) =>
        getScopesForFuelType(allScopesForBus, scopeToFuelTypesForBus, type);

    const availableScopes = getAvailableScopesForType(selectedGeneratorType);
    // Intentionally NOT isLocalStudyBaseEligible. This grouping is "who made
    // it", not "can you re-study from it": a custom scope (subscription, no
    // user_scope_config) is Nira-built and belongs under "Nira Scopes" here,
    // even though SelectBasePanel correctly refuses to use it as a base scope.
    const niraScopes = availableScopes.filter((s) => !isLocalStudy(s));
    const userScopes = availableScopes.filter(isLocalStudy);

    const handleGeneratorTypeChange = (type: GeneratorType) => {
        if (type !== selectedGeneratorType) {
            segmentTrackBusPageFuelTypeChanged(niraBusId, bus.region, type);
        }
        setSelectedGeneratorType(type);
        const newAvailableScopes = getAvailableScopesForType(type);
        const currentScopeStillValid = newAvailableScopes.some(
            (s) => s.scope === selectedScope?.scope
        );
        if (!currentScopeStillValid) {
            setSelectedScope(newAvailableScopes[0]?.scope);
        }
    };

    const handleScopeChange = (scopeName: string) => {
        const newScope = availableScopes.find((s) => s.scope === scopeName);
        if (newScope !== undefined) {
            segmentTrackBusPageScopeChanged(
                niraBusId,
                bus.region,
                newScope.scope,
                newScope.userScopeConfig === undefined
                    ? "nira_scope"
                    : "user_study"
            );
        }
        setSelectedScope(scopeName);
    };

    // Project-size inputs update on every keystroke and stepper-button click,
    // so the analytics event is debounced per resource type: one event per
    // editing burst, carrying the final value. Debouncing on value changes
    // (rather than tracking on blur) also captures stepper-button edits,
    // which never focus or blur the text input.
    const projectSizeTrackTimers = useRef(
        new Map<ResourceType, { timerId: number; flush: () => void }>()
    );
    useEffect(() => {
        const timers = projectSizeTrackTimers.current;
        return () => {
            // On unmount (e.g. in-app navigation away from the bus page),
            // flush any still-pending edit instead of dropping it, so an
            // editing burst that ended within the debounce window still
            // emits. Best-effort: a hard tab close/refresh can't be caught
            // here (no sendBeacon), but in-app navigation is the common case.
            const pending = Array.from(timers.values());
            timers.clear();
            pending.forEach(({ timerId, flush }) => {
                window.clearTimeout(timerId);
                flush();
            });
        };
    }, []);
    const trackProjectSizeChangeDebounced = (
        resourceType: ResourceType,
        projectSizeMw: number
    ) => {
        const timers = projectSizeTrackTimers.current;
        const pending = timers.get(resourceType);
        // NumericInput emits NaN on empty/transient input (e.g. a cleared or
        // half-typed field), so never track a NaN project size. A cleared or
        // invalidated field also ends the current editing burst, so cancel any
        // pending event rather than letting a now-stale value flush later
        // (on timeout or on the unmount flush).
        if (Number.isNaN(projectSizeMw)) {
            if (pending !== undefined) {
                window.clearTimeout(pending.timerId);
                timers.delete(resourceType);
            }
            return;
        }
        if (pending !== undefined) {
            window.clearTimeout(pending.timerId);
        }
        const flush = () => {
            timers.delete(resourceType);
            segmentTrackBusPageProjectSizeChanged(
                niraBusId,
                bus.region,
                resourceType,
                projectSizeMw
            );
        };
        const timerId = window.setTimeout(
            flush,
            PROJECT_SIZE_TRACK_DEBOUNCE_MS
        );
        timers.set(resourceType, { timerId, flush });
    };

    const { showCapacity, showChargingSize } = pricedConstraintConfig;
    const shouldShowCharging =
        selectedGeneratorType === STORAGE_TYPE && showChargingSize;

    return (
        <Card className="BusConfigurationCard">
            <h5 className="bp5-heading BusConfigurationCard-title">
                Configuration
            </h5>
            <div className="BusConfigurationCard-sections">
                <div className="BusConfigurationCard-section">
                    <h6 className="bp5-heading">Fuel Type</h6>
                    <div className="BusConfigurationCard-fuel-types">
                        {getGenerationFuelTypesForRegion(bus.region).map(
                            (type: GeneratorType) => (
                                <Tag
                                    key={type}
                                    interactive
                                    round
                                    minimal={selectedGeneratorType !== type}
                                    intent={
                                        selectedGeneratorType === type
                                            ? "primary"
                                            : "none"
                                    }
                                    onClick={() =>
                                        handleGeneratorTypeChange(type)
                                    }
                                    icon={
                                        <GeneratorTypeIcon
                                            type={type}
                                            size={12}
                                        />
                                    }
                                >
                                    {getLabelForRegionAndGeneratorType(
                                        bus.region,
                                        type
                                    )}
                                </Tag>
                            )
                        )}
                    </div>
                </div>
                <div className="BusConfigurationCard-section">
                    <h6 className="bp5-heading">Scope</h6>
                    <HTMLSelect
                        value={selectedScope?.scope ?? ""}
                        onChange={(e) => handleScopeChange(e.target.value)}
                        fill
                        disabled={availableScopes.length === 0}
                    >
                        <option value="" disabled>
                            {availableScopes.length === 0
                                ? "No scopes available"
                                : "Select a scope"}
                        </option>
                        {userScopes.length === 0
                            ? renderScopeOptions(availableScopes)
                            : [
                                  <optgroup key="nira" label="Nira Scopes">
                                      {renderScopeOptions(niraScopes)}
                                  </optgroup>,
                                  <optgroup key="user" label="My Studies">
                                      {renderScopeOptions(userScopes)}
                                  </optgroup>
                              ]}
                    </HTMLSelect>
                </div>
                <div className="BusConfigurationCard-section">
                    <h6 className="bp5-heading">Project Size</h6>
                    <div className="BusConfigurationCard-project-size">
                        <span>Energy (MW)</span>
                        <NumericInput
                            value={powerAmounts.energySize}
                            onValueChange={(value) => {
                                setPowerAmounts({
                                    ...powerAmounts,
                                    energySize: value
                                });
                                trackProjectSizeChangeDebounced(
                                    ENERGY_RESOURCE_TYPE,
                                    value
                                );
                            }}
                            buttonPosition="right"
                            allowNumericCharactersOnly
                            stepSize={STEP_SIZE}
                            max={MAX_MW}
                            min={MIN_MW}
                            fill
                        />
                        {shouldShowCharging && (
                            <>
                                <span>Charging (MW)</span>
                                <NumericInput
                                    value={powerAmounts.chargingSize}
                                    onValueChange={(value) => {
                                        setPowerAmounts({
                                            ...powerAmounts,
                                            chargingSize: value
                                        });
                                        trackProjectSizeChangeDebounced(
                                            CHARGING_RESOURCE_TYPE,
                                            value
                                        );
                                    }}
                                    buttonPosition="right"
                                    allowNumericCharactersOnly
                                    stepSize={STEP_SIZE}
                                    max={MAX_MW}
                                    min={MIN_MW}
                                    fill
                                />
                            </>
                        )}
                        {showCapacity && (
                            <>
                                <span>Capacity (MW)</span>
                                <NumericInput
                                    value={powerAmounts.capacitySize}
                                    onValueChange={(value) => {
                                        setPowerAmounts({
                                            ...powerAmounts,
                                            capacitySize: value
                                        });
                                        trackProjectSizeChangeDebounced(
                                            CAPACITY_RESOURCE_TYPE,
                                            value
                                        );
                                    }}
                                    buttonPosition="right"
                                    allowNumericCharactersOnly
                                    stepSize={STEP_SIZE}
                                    max={MAX_MW}
                                    min={MIN_MW}
                                    fill
                                />
                            </>
                        )}
                    </div>
                </div>
            </div>
        </Card>
    );
};

export default BusConfigurationCard;
